import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  image_url?: string;
  parent_id?: number | null;
  sort_order: number;
  is_active: boolean;
  product_count?: number;
  created_at: string;
  updated_at: string;
}

const CategoriesWrapper = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const CategoriesHeader = styled.div`
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-border);

  h2 {
    margin: 0 0 var(--spacing-4) 0;
    color: var(--color-text);
    font-size: var(--font-size-xl);
    font-weight: 600;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  gap: var(--spacing-4);
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: var(--spacing-2) var(--spacing-3);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  width: 300px;
  
  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
  }
`;

const AddButton = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-2) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #1f1e3a;
  }
`;

const CategoriesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    text-align: left;
    padding: var(--spacing-3) var(--spacing-4);
    border-bottom: 1px solid var(--color-border);
  }
  
  th {
    background: #f8f9fa;
    font-weight: 600;
    color: var(--color-text);
    font-size: var(--font-size-sm);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  tbody tr:hover {
    background: #f8f9fa;
  }
`;

const StatusBadge = styled.span<{ active: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${({ active }) => active
    ? 'background: #dcfce7; color: #166534;'
    : 'background: #fee2e2; color: #991b1b;'
  }
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text);
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--border-radius);
  font-size: var(--font-size-xs);
  cursor: pointer;
  margin-right: var(--spacing-2);
  
  &:hover {
    ${({ variant }) => variant === 'delete'
      ? 'background: #fee2e2; border-color: #fecaca; color: #dc2626;'
      : 'background: #f3f4f6; border-color: #d1d5db;'
    }
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-4);
  
  h3 {
    margin: 0;
    color: var(--color-secondary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  color: var(--color-text-light);
  
  &:hover {
    color: var(--color-text);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  
  label {
    font-weight: 500;
    color: var(--color-text);
  }
  
  input, textarea, select {
    padding: var(--spacing-3);
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    font-size: var(--font-size-sm);
    
    &:focus {
      outline: none;
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  padding-top: var(--spacing-4);
  border-top: 1px solid var(--color-border);
`;

const SubmitButton = styled.button`
  background-color: var(--color-secondary);
  color: white;
  padding: var(--spacing-3) var(--spacing-6);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #1f1e3a;
  }
  
  &:disabled {
    background-color: var(--color-text-light);
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid var(--color-border);
  color: var(--color-text);
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  cursor: pointer;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-text-light);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: var(--spacing-8);
  color: var(--color-error);
`;

const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await apiService.getCategories({ with_count: true });
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Kunde inte ladda kategorier. Kontrollera att servern är igång.');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[åä]/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Remove duplicate hyphens
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const slug = generateSlug(formData.name);
    const categoryData = { ...formData, slug };

    try {
      setSubmitting(true);
      
      if (editingCategory) {
        await apiService.updateCategory(editingCategory.id, categoryData);
      } else {
        await apiService.createCategory(categoryData);
      }

      await fetchCategories();
      handleCloseModal();
      alert(editingCategory ? 'Kategori uppdaterad!' : 'Kategori skapad!');
    } catch (err) {
      console.error('Error saving category:', err);
      alert('Kunde inte spara kategori. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      image_url: category.image_url || '',
      sort_order: category.sort_order,
      is_active: category.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Är du säker på att du vill ta bort kategorin "${category.name}"?${category.product_count ? `\n\nDenna kategori har ${category.product_count} produkt(er).` : ''}`)) {
      return;
    }

    try {
      await apiService.deleteCategory(category.id);
      await fetchCategories();
      alert('Kategori borttagen!');
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Kunde inte ta bort kategori. Försök igen.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      sort_order: 0,
      is_active: true
    });
  };

  const handleOpenAddModal = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      image_url: '',
      sort_order: categories.length,
      is_active: true
    });
    setShowModal(true);
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <CategoriesWrapper>
        <LoadingMessage>Laddar kategorier...</LoadingMessage>
      </CategoriesWrapper>
    );
  }

  if (error) {
    return (
      <CategoriesWrapper>
        <ErrorMessage>{error}</ErrorMessage>
      </CategoriesWrapper>
    );
  }

  return (
    <>
      <CategoriesWrapper>
        <CategoriesHeader>
          <h2>🗂️ Kategorier</h2>
          
          <ControlsWrapper>
            <SearchInput
              type="text"
              placeholder="Sök kategorier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <AddButton onClick={handleOpenAddModal}>
              ➕ Ny kategori
            </AddButton>
          </ControlsWrapper>
        </CategoriesHeader>

        <div style={{ overflowX: 'auto' }}>
          {filteredCategories.length > 0 ? (
            <CategoriesTable>
              <thead>
                <tr>
                  <th>Namn</th>
                  <th>Beskrivning</th>
                  <th>Slug</th>
                  <th>Produkter</th>
                  <th>Sortering</th>
                  <th>Status</th>
                  <th>Skapad</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category) => (
                  <tr key={category.id}>
                    <td style={{ fontWeight: '500' }}>{category.name}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {category.description || '-'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)' }}>
                      {category.slug}
                    </td>
                    <td>{category.product_count || 0} st</td>
                    <td>{category.sort_order}</td>
                    <td>
                      <StatusBadge active={category.is_active}>
                        {category.is_active ? 'Aktiv' : 'Inaktiv'}
                      </StatusBadge>
                    </td>
                    <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-light)' }}>
                      {new Date(category.created_at).toLocaleDateString('sv-SE')}
                    </td>
                    <td>
                      <ActionButton onClick={() => handleEdit(category)}>
                        ✏️ Redigera
                      </ActionButton>
                      <ActionButton 
                        variant="delete" 
                        onClick={() => handleDelete(category)}
                      >
                        🗑️ Ta bort
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </CategoriesTable>
          ) : (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-text-light)' }}>
              {searchTerm ? 'Inga kategorier matchar din sökning.' : 'Inga kategorier hittades.'}
            </div>
          )}
        </div>
      </CategoriesWrapper>

      <Modal isOpen={showModal}>
        <ModalContent>
          <ModalHeader>
            <h3>{editingCategory ? 'Redigera kategori' : 'Ny kategori'}</h3>
            <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <label>Namn *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="T.ex. Ljus & Doftljus"
              />
            </FormGroup>

            <FormGroup>
              <label>Beskrivning</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Beskriv kategorin..."
                rows={3}
              />
            </FormGroup>

            <FormGroup>
              <label>Bild-URL</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </FormGroup>

            <FormGroup>
              <label>Sorteringsordning</label>
              <input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </FormGroup>

            <FormGroup>
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ marginRight: 'var(--spacing-2)' }}
                />
                Aktiv
              </label>
            </FormGroup>

            <FormActions>
              <CancelButton type="button" onClick={handleCloseModal}>
                Avbryt
              </CancelButton>
              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? 'Sparar...' : (editingCategory ? 'Uppdatera' : 'Skapa')}
              </SubmitButton>
            </FormActions>
          </Form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminCategories;